import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, Text } from '@components'
import { useLanguage } from '@context'
import { linkedinRedirectUri, translateApiError } from '@utils'
import { ScreenProps } from '@navigation'
import { useCompleteEventSignIn, useJoinEventWithLinkedIn, useStartEventPayment } from '@hooks'

/**
 * `/linkedin/callback?code=…&state=<event code>` — where LinkedIn sends the
 * member back. Exchanges the code (server side), logs in, then opens the event.
 */
export const LinkedInCallback: React.FC<ScreenProps<'LinkedInCallback'>> = ({
  route,
  navigation,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const join = useJoinEventWithLinkedIn()
  const completeSignIn = useCompleteEventSignIn()
  const { startPayment } = useStartEventPayment()
  const [error, setError] = useState('')
  const started = useRef(false)
  const eventCode = String(route.params?.state ?? '')

  useEffect(() => {
    if (started.current) return
    started.current = true
    const authorizationCode = route.params?.code
    if (!authorizationCode || !eventCode || route.params?.error) {
      setError(t('events.join.linkedinCallbackError'))
      return
    }
    join.mutate(
      { eventCode, authorizationCode, redirectUri: linkedinRedirectUri(window.location.origin) },
      {
        onSuccess: async (session) => {
          await completeSignIn(session)
          navigation.reset({
            index: 0,
            routes: [{ name: 'PublicEvent', params: { code: eventCode } }],
          })
          // Paid event: identity done, next step is the payment page.
          if (session.attendance?.paymentRequired) await startPayment({ code: eventCode })
        },
        onError: (err) => setError(translateApiError(t, err).message),
      },
    )
  }, [completeSignIn, eventCode, join, navigation, route.params, startPayment, t])

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {error ? (
        <>
          <Text type="titleBold" center>
            {error}
          </Text>
          {eventCode ? (
            <Button
              title={t('events.join.back')}
              onPress={() =>
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'PublicEvent', params: { code: eventCode } }],
                })
              }
            />
          ) : null}
        </>
      ) : (
        <>
          <ActivityIndicator color={theme.colors.primary} />
          <Text type="body" center color={theme.colors.greyBlue}>
            {t('events.join.linkedinLoading')}
          </Text>
        </>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 },
})
